import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";
import { Layout } from "./components/Layout";
import { Anmelden, LoginWahl, Bestaetigen, Einladung, PasswortNeu, PasswortVergessen, Registrieren, RegistrierungGesendet } from "./pages/Auth";
import { Home } from "./pages/Home";
import { Privat } from "./pages/Privat";
import { PrivatPreise, PrivatRecht, PrivatRegistrieren } from "./pages/PrivatSeiten";
import { Erklaert } from "./pages/Erklaert";
import { Datensicherheit, Funktionen, Kontakt, NotFound, Preise, UeberUns, WebApp } from "./pages/Info";
import { Rechtliches } from "./pages/Rechtliches";
import { Stand } from "./pages/Stand";
import { BlogArticle, BlogList } from "./pages/Blog";
import { Kundenbereich } from "./pages/Kundenbereich";
import { Abo } from "./pages/Abo";
import { Unterlagen } from "./pages/Unterlagen";
import { Akte } from "./pages/Akte";
import { Uebergabe } from "./pages/Uebergabe";
import { BelegLink } from "./pages/BelegLink";
import { MobileApp } from "./app/MobileApp";
import { ConnectPhone } from "./app/PhoneLink";
import { DesktopApp } from "./app/Desktop";
import { DesktopLogin } from "./app/DesktopLogin";

export function App() {
  return (
    <Routes>
      <Route path="/app/verbinden" element={<ConnectPhone />} />
      <Route path="/unterlagen/:token" element={<Unterlagen />} />
      <Route path="/akte/:token" element={<Akte />} />
      <Route path="/uebergabe/:token" element={<Uebergabe />} />
      <Route path="/beleg/:token" element={<BelegLink />} />
      <Route path="/app/*" element={<MobileApp />} />
      <Route path="/desktop/anmelden" element={<DesktopLogin />} />
      <Route path="/desktop/*" element={<DesktopApp />} />
      <Route path="*" element={<Website />} />
    </Routes>
  );
}

// Titel und Beschreibung je Seite, damit Suchmaschinen sie unterscheiden können. Blog-Seiten setzen ihren Titel selbst.
const SEITEN: Record<string, { title: string; text: string }> = {
  "/": { title: "HandVero – Software für Handwerksbetriebe", text: "HandVero ordnet alles, was zu einem Auftrag gehört: Fotos, PDFs, Termine und Absprachen. Am PC im Büro und mit dem Smartphone auf der Baustelle." },
  "/privat": { title: "HandVero Zuhause – die Hausakte für Privatleute", text: "Fotos von Leitungen, Typenschilder, Rechnungen und Wartungstermine fürs Zuhause an einem Ort. Zum Start kostenlos." },
  "/privat/preise": { title: "Preise – HandVero Zuhause", text: "Kostenlos mit 1 GB Speicher oder Plus für 1,99 € im Monat mit mehr Platz und ohne Werbung." },
  "/privat/registrieren": { title: "Kostenlos anmelden – HandVero Zuhause", text: "Hausakte für Privatpersonen anlegen. Kostenlos, ohne Zahlungsdaten." },
  "/privat/agb": { title: "Nutzungsbedingungen – HandVero Zuhause", text: "Bedingungen und Datenschutz für Privatpersonen, einfach erklärt." },
  "/privat/erklaert": { title: "Vero erklärt – HandVero Zuhause", text: "Ehrliche Antworten: Warum Elektrik geprüft wird, was eine Heizungswartung bringt, wie oft Rauchmelder zu prüfen sind." },
  "/funktionen": { title: "Funktionen – HandVero", text: "Fotos direkt in den richtigen Projektordner, Dokumente, Termine und Absprachen: Das kann HandVero für Ihren Betrieb." },
  "/web-app": { title: "App für iPhone und Android – HandVero", text: "Die HandVero-App auf dem Startbildschirm Ihres Smartphones: Fotos aufnehmen, Dokumente finden, Termine sehen." },
  "/preise": { title: "Preise – HandVero", text: "Die ersten 100 Betriebe starten kostenlos. Hier sehen Sie, was in welchem Paket enthalten ist." },
  "/datensicherheit": { title: "Ihre Daten bei HandVero", text: "Wo Ihre Daten liegen, wer sie sieht und was wir nicht tun, in einfacher Sprache." },
  "/ueber-uns": { title: "Über uns – HandVero", text: "HandVero kommt aus dem Erzgebirge und wird aus dem Handwerk heraus entwickelt." },
  "/kontakt": { title: "Kontakt – HandVero", text: "So erreichen Sie HandVero bei Fragen zur Software." },
  "/stand": { title: "Stand heute – HandVero", text: "Was HandVero heute schon kann und was noch kommt." },
  "/rechtliches": { title: "Rechtliches – HandVero", text: "Impressum, Datenschutz, Nutzungsbedingungen und Auftragsverarbeitung von HandVero." },
};

function SeitenKopf() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/blog")) return;
    const seite = SEITEN[pathname.replace(/\/+$/, "") || "/"];
    if (!seite) return;
    document.title = seite.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", seite.text);
  }, [pathname]);
  return null;
}

function Website() {
  return (
    <Layout>
      <SeitenKopf />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privat" element={<Privat />} />
        <Route path="/privat/erklaert" element={<Erklaert />} />
        <Route path="/privat/preise" element={<PrivatPreise />} />
        <Route path="/privat/registrieren" element={<PrivatRegistrieren />} />
        <Route path="/privat/agb" element={<PrivatRecht />} />
        <Route path="/funktionen" element={<Funktionen />} />
        <Route path="/web-app" element={<WebApp />} />
        <Route path="/preise" element={<Preise />} />
        <Route path="/ueber-uns" element={<UeberUns />} />
        <Route path="/datensicherheit" element={<Datensicherheit />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/stand" element={<Stand />} />
        <Route path="/rechtliches" element={<Rechtliches />} />
        <Route path="/registrieren" element={<Registrieren />} />
        <Route path="/registrierung-gesendet" element={<RegistrierungGesendet />} />
        <Route path="/bestaetigen" element={<Bestaetigen />} />
        <Route path="/login" element={<LoginWahl />} />
        <Route path="/anmelden" element={<Anmelden />} />
        <Route path="/passwort-vergessen" element={<PasswortVergessen />} />
        <Route path="/passwort-neu" element={<PasswortNeu />} />
        <Route path="/einladung" element={<Einladung />} />
        <Route path="/kundenbereich" element={<Kundenbereich />} />
        <Route path="/kundenbereich/abo" element={<Abo />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}
